import { Map } from 'immutable'
import { where, pickToJS } from 'utils/immutable'
import {
  CAN,
  PIN,
  PWM,
  SERVICE,
  DO_PIN_NON_PWM,
  DO_PIN_PWM,
  NATIVE_BOOL,
  NATIVE_NON_BOOL,
  CAN_TWO_BIT,
  CAN_SIXTEEN_BIT,
  CAN_OTHER_BIT,
  SWITCH_INPUT,
} from 'utils/constants'
import { equals, both, complement, isNil, path } from 'ramda'
import { isPinInput } from 'utils/types/features'

export const formatSignalDescription = (signal) => {
  const description = signal.get('description')
  if (isPinInput(signal.get('dataType'))) {
    return `${description}: ${signal.get('connector')}-${signal.get(
      'location'
    )}`
  }

  return description
}

export const transformSwitchInputData = (signal) => {
  const {
    uri,
    relatedInputs,
    interface: switchPositions,
    description,
  } = pickToJS(['uri', 'relatedInputs', 'interface', 'description'], signal)
  const relatedInputUri = !isNil(relatedInputs)
    ? Object.keys(relatedInputs)[0]
    : null
  const positionUp = isNil(relatedInputUri)
    ? null
    : path(['switchPositionDictionary', 'switch_up'], switchPositions)
  const positionDown = isNil(relatedInputUri)
    ? null
    : path(['switchPositionDictionary', 'switch_down'], switchPositions)
  const isUpPosition =
    equals(positionUp, 1) || isNil(positionUp) || !equals(positionDown, 1)
  const upUri = isUpPosition ? uri : relatedInputUri
  const downUri = isUpPosition ? relatedInputUri : uri
  const transformedSignal = Map({
    uriOptionsByPosition: [
      { value: upUri, position: 'up' },
      { value: downUri, position: 'down' },
    ],
    description,
    uri,
    inputType: SWITCH_INPUT,
  })
  return transformedSignal
}

const isTwoBitCan = where({
  numberOfBits: equals(2),
  type: equals(CAN),
})
const isSixteenBitCan = where({
  numberOfBits: equals(16),
  type: equals(CAN),
})
const isOtherBitCan = where({
  type: equals(CAN),
  numberOfBits: both(complement(equals(16)), complement(equals(2))),
})

const isPWMPin = where({
  type: equals(PIN),
  polarity: equals(PWM),
})
const isNonPWMPin = where({
  type: equals(PIN),
  polarity: complement(equals(PWM)),
})
const isBooleanNative = where({
  type: equals(SERVICE),
  isOutputBoolean: equals(true),
})
const isNonBooleanNative = where({
  type: equals(SERVICE),
  isOutputBoolean: equals(false),
})

const OUTPUT_CONFIGS = Map({
  [DO_PIN_PWM]: isPWMPin,
  [DO_PIN_NON_PWM]: isNonPWMPin,
  [NATIVE_BOOL]: isBooleanNative,
  [NATIVE_NON_BOOL]: isNonBooleanNative,
  [CAN_TWO_BIT]: isTwoBitCan,
  [CAN_SIXTEEN_BIT]: isSixteenBitCan,
  [CAN_OTHER_BIT]: isOtherBitCan,
})

const outputConfigsWithLimitedInputs = new Set([
  NATIVE_NON_BOOL,
  CAN_SIXTEEN_BIT,
])
const outputConfigsWithRestrictedInputs = new Set([NATIVE_BOOL])

export const getOutputConfig = (output) =>
  OUTPUT_CONFIGS.findKey((testFn) => testFn(output))

export const LIMITED = 'LIMITED'
export const RESTRICTED = 'RESTRICTED'

/**
 * This function determines the high level availability
 * of output-controlling inputs as defined by this wiki page
 * https://dtna.visualstudio.com/chec/_wiki/wikis/CHEC.wiki/695/CHECplus-Feature-Input-Driving-Output-Matrix
 * Specifically, the IO allowance matrix attributes v 6 spreadsheet
 * Some of the input types are more finely restricted. That
 * determination is made in downstream/parallel logic
 */
export const determineDisabledInputOptions = (output) => {
  const outputConfig = getOutputConfig(output)
  if (outputConfigsWithRestrictedInputs.has(outputConfig)) {
    /**
     * per the above referenced spreadsheet:
     * for non-ASAM features:
     * Native Outputs that are booleans
     * for ASAM features:
     * Native Outputs that are booleans
     * _cannot_ be controlled by AI Pin inputs
     */
    return RESTRICTED
  }
  if (outputConfigsWithLimitedInputs.has(outputConfig)) {
    /**
     * per the above referenced spreadsheet,
     * for non-ASAM features:
     * Native Outputs that are NOT booleans,
     * DO Pin with PWM current,
     * and 16 Bit CAN outputs
     * for ASAM features:
     * Native Outputs that are NOT booleans,
     * and 16 Bit CAN outputs
     * _cannot_ be controlled by DI Pin inputs
     */
    return LIMITED
  }

  /**
   * all other outputs can be all the things
   */
  return null
}
